import { Assets, Sprite, type Texture } from 'pixi.js';
import { music } from './music';
import { sound } from './sound';

/**
 * Optional bitmap art on top of the vector placeholders. A pack is a folder under public/art/<pack>/
 * with a manifest.json that maps asset keys to PNG files; every key the pack lacks keeps its vector
 * drawing, so art can land piece by piece. Keys: tile:grass, tile:dirt, rock, bunker,
 * unit:<archetype>, enemy:<archetype>, soldier:<spawner archetype>, hero:<hero id>.
 *
 * manifest.json: { "frames": { "unit:rapid": "unit_rapid.png", "enemy:boss": { "file": "boss.png", "scale": 1.3 } } }
 * `scale` multiplies the size the renderer would give the sprite; use it when a sprite has a lot of empty margin.
 */
type FrameDef = string | { file: string; scale?: number };

export interface FrameSet {
  walk: Texture[];
  attack: Texture[];
  /** Height of the tallest frame; all frames are scaled as if they were this tall. */
  refH: number;
}

export class ArtPack {
  private textures = new Map<string, Texture>();
  private scales = new Map<string, number>();
  /** Pack id that loaded, or null when the game runs on vectors. */
  loaded: string | null = null;

  async load(pack: string): Promise<void> {
    const base = `${import.meta.env.BASE_URL}art/${pack}/`;
    let manifest: { frames?: Record<string, FrameDef>; audio?: Record<string, string> };
    try {
      const res = await fetch(`${base}manifest.json`, { cache: 'no-cache' });
      if (!res.ok) return;
      manifest = (await res.json()) as { frames?: Record<string, FrameDef>; audio?: Record<string, string> };
    } catch {
      return;
    }
    music.usePack(base, manifest.audio);
    sound.usePack(base, manifest.audio);
    const entries = Object.entries(manifest.frames ?? {});
    await Promise.all(
      entries.map(async ([key, def]) => {
        const file = typeof def === 'string' ? def : def.file;
        try {
          const tex = await Assets.load<Texture>({ src: `${base}${file}`, loadParser: 'loadTextures' });
          this.textures.set(key, tex);
          if (typeof def !== 'string' && def.scale) this.scales.set(key, def.scale);
        } catch {
          /* a missing file keeps its vector drawing */
        }
      }),
    );
    if (this.textures.size > 0) this.loaded = pack;
  }

  has(key: string): boolean {
    return this.textures.has(key);
  }

  /**
   * Animation frames for an actor: `anim:<name>:w1..w4` (walk) and `anim:<name>:a1..a2` (attack).
   * Frames are cut to their own bounds, so every frame is drawn at one shared scale (from the tallest
   * frame) and anchored at the feet, keeping the character the same size across the cycle.
   * Null when the pack has no walk frames for this name.
   */
  frames(name: string): FrameSet | null {
    const cached = this.frameSets.get(name);
    if (cached !== undefined) return cached;
    const walk: Texture[] = [];
    for (let i = 1; i <= 8; i++) {
      const t = this.textures.get(`anim:${name}:w${i}`);
      if (!t) break;
      walk.push(t);
    }
    const attack: Texture[] = [];
    for (let i = 1; i <= 4; i++) {
      const t = this.textures.get(`anim:${name}:a${i}`);
      if (!t) break;
      attack.push(t);
    }
    if (walk.length < 2) {
      this.frameSets.set(name, null);
      return null;
    }
    const refH = Math.max(...[...walk, ...attack].map((t) => t.height));
    const set = { walk, attack, refH };
    this.frameSets.set(name, set);
    return set;
  }

  private frameSets = new Map<string, FrameSet | null>();

  /** A centred sprite fitted inside w × h (contain), or null when the pack has no such key. */
  sprite(key: string, w: number, h: number): Sprite | null {
    const tex = this.textures.get(key);
    if (!tex) return null;
    const sp = new Sprite(tex);
    sp.anchor.set(0.5);
    this.fit(sp, key, w, h);
    return sp;
  }

  /** Resize an existing sprite of this key to fit w × h. */
  fit(sp: Sprite, key: string, w: number, h: number): void {
    const tex = sp.texture;
    const k = Math.min(w / tex.width, h / tex.height) * (this.scales.get(key) ?? 1);
    sp.scale.set(k);
  }
}

export const art = new ArtPack();
